// Implements: F-UIX-019, F-IDN-027
/**
 * "¿Suspender …?" / "¿Reactivar …?" (v3 `orgStatus`): what the change does to the organization's
 * people and links, said before it happens. Suspending deletes nothing; reactivating restores both.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import type { OrganizationView } from "../../../types/organization";
import styles from "./Organization.module.css";

export interface OrgStatusDialogProps {
  readonly organization: OrganizationView;
  readonly pending: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function OrgStatusDialog({ organization, pending, onConfirm, onCancel }: OrgStatusDialogProps): ReactElement {
  const { t } = useTranslation();
  const suspending = organization.status === "active";
  const params = { name: organization.name, count: organization.member_count };
  return (
    <Dialog
      open
      title={
        suspending ? t("organization.platform.suspend.title", params) : t("organization.platform.reactivate.title", params)
      }
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {t("common.action.cancel")}
          </Button>
          <Button variant={suspending ? "danger" : "primary"} loading={pending} onClick={onConfirm}>
            {suspending ? t("organization.platform.suspend") : t("organization.platform.reactivate")}
          </Button>
        </>
      }
    >
      <p className={styles.status}>
        {suspending ? t("organization.platform.suspend.body", params) : t("organization.platform.reactivate.body", params)}
      </p>
    </Dialog>
  );
}
